import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Clipboard, ClipboardCheck, MapPin, ArrowRight, Sparkles, History, LayoutGrid, Check, ArrowDownToLine, ArrowUpFromLine, UserCircle2, Route, Clock, Loader2, Link2, Copy, QrCode } from "lucide-react";
import { attachAutoClearOnAppOpen } from "@/lib/pushDismiss";
import PushToggle from "@/components/PushToggle";
import PushEnableBanner from "@/components/PushEnableBanner";
import EnableNotificationsButton from "@/components/EnableNotificationsButton";
import AddressAutocomplete from "@/components/AddressAutocomplete";
import PhoneInput from "@/components/PhoneInput";
import ClientHero from "@/components/ClientHero";
import HotelProfileMenu from "@/components/HotelProfileMenu";

// DASH_BG removed — replaced by the animated ClientHero (idle/searching/tracking).

const BOOKING_DRAFT_KEY = "saor:hotel-booking-draft";

const defaultForm = {
  arrival_address: "", arrival_coord: null, client_name: "",
  luggage_count: 1, passenger_count: 1, client_phone: "", notes: "",
  vehicle_type: "sedan",
  booking_type: "immediate", scheduled_at: "",
  direction: "from_hotel", // from_hotel = pickup at hotel ; to_hotel = drop at hotel
  receptionist_id: "",
  // Dispatch-only fields (ignored for "hotel"/"airbnb" accounts)
  pickup_address: "", pickup_coord: null,
  custom_price: "",
  dispatch_commission_pct: 20,
  extras: [],
};

const EXTRA_OPTIONS = [
  { value: "child_seat",   label: "Siège bébé" },
  { value: "booster",      label: "Réhausseur" },
  { value: "animal",       label: "Animal" },
  { value: "wheelchair",   label: "Fauteuil roulant" },
  { value: "extra_luggage", label: "Bagages volumineux" },
];

/**
 * Hotel dashboard = Quick booking only (no more tabs).
 * History has its own page at /dashboard/hotel/history.
 */
export default function HotelDashboard() {
  const { profile } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState(() => {
    try {
      const raw = sessionStorage.getItem(BOOKING_DRAFT_KEY);
      if (raw) return { ...defaultForm, ...JSON.parse(raw) };
    } catch (_) {}
    return defaultForm;
  });
  const [loading, setLoading] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [copied, setCopied] = useState(false);
  const [receptionists, setReceptionists] = useState([]);
  // Active booking (the most recent one still in flight) — drives the
  // ClientHero variant : idle when none, searching when pending, tracking
  // when a driver has accepted. Polls every 12 s so the radar appears
  // automatically right after the hotel submits a request.
  const [activeBooking, setActiveBooking] = useState(null);
  // Live price preview — recomputed every time the form changes (debounced
  // 500 ms). Shown next to the submit button so the receptionist sees the
  // price BEFORE clicking "Réserver", same UX as the client dashboard.
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);

  // Load the receptionists the admin has configured for this hotel. Airbnb
  // accounts always return an empty list — the selector is then hidden.
  useEffect(() => attachAutoClearOnAppOpen(), []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get("/hotel/receptionists");
        if (!cancelled) setReceptionists(r.data.receptionists || []);
      } catch (_) {
        if (!cancelled) setReceptionists([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Persist the draft every time the form changes so we keep it across
  // navigations (e.g., hotel switches to history and back).
  useEffect(() => {
    try { sessionStorage.setItem(BOOKING_DRAFT_KEY, JSON.stringify(form)); } catch (_) {}
  }, [form]);

  // Poll the hotel's bookings every 12 s and surface the most recent
  // in-flight ride so the ClientHero can switch from idle → searching →
  // tracking automatically. Stays mounted for the lifetime of the page.
  useEffect(() => {
    let alive = true;
    const refresh = async () => {
      try {
        const r = await api.get("/hotel/bookings");
        const list = r.data?.bookings || [];
        // Most recent ride that's still "live" from the hotel's POV.
        const live = list.find((b) =>
          ["pending", "accepted", "picked_up", "in_progress"].includes(b.status)
        );
        if (alive) setActiveBooking(live || null);
      } catch (_) { /* silent — non-blocking visual */ }
    };
    refresh();
    const t = setInterval(refresh, 12_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // When a driver accepts the just-created course, the hotel doesn't care
  // about the form anymore — they want to track it. Redirect them to the
  // history page automatically the moment the status flips to "accepted"
  // (or anything past it). The ref guard prevents looping back to history
  // if the user manually returns to the dashboard later.
  const lastSeenStatusRef = useRef(null);
  useEffect(() => {
    if (!activeBooking) { lastSeenStatusRef.current = null; return; }
    const prev = lastSeenStatusRef.current;
    const curr = activeBooking.status;
    lastSeenStatusRef.current = curr;
    if (prev === "pending" && ["accepted", "picked_up", "in_progress"].includes(curr)) {
      toast.success("Un chauffeur a accepté votre course !");
      nav("/dashboard/hotel/history", { replace: false });
    }
  }, [activeBooking?.status, nav]);

  const heroMode = activeBooking
    ? (["accepted", "picked_up", "in_progress"].includes(activeBooking.status) ? "tracking" : "searching")
    : "idle";

  // Live price preview — debounced quote whenever the form changes enough
  // to compute a price. We require an arrival_coord (the autocomplete sets
  // it as soon as the user picks a result). Vehicle type / direction /
  // custom price / dispatch commission impact pricing too.
  useEffect(() => {
    if (!form.arrival_coord) { setQuote(null); return; }
    let alive = true;
    setQuoteLoading(true);
    const t = setTimeout(async () => {
      try {
        const payload = {
          arrival_address: form.arrival_address,
          arrival_coord: form.arrival_coord,
          client_name: form.client_name || "Quote",
          luggage_count: form.luggage_count || 0,
          passenger_count: form.passenger_count || 1,
          client_phone: form.client_phone || "",
          notes: "",
          vehicle_type: form.vehicle_type,
          booking_type: form.booking_type,
          scheduled_at: form.scheduled_at || null,
          direction: form.direction,
          receptionist_id: form.receptionist_id || null,
          pickup_address: form.pickup_address || null,
          pickup_coord: form.pickup_coord || null,
          custom_price: form.custom_price !== "" ? Number(form.custom_price) : null,
          dispatch_commission_pct: form.dispatch_commission_pct,
          extras: form.extras || [],
        };
        const r = await api.post("/hotel/quote", payload);
        if (alive) setQuote(r.data);
      } catch (_) {
        if (alive) setQuote(null);
      } finally {
        if (alive) setQuoteLoading(false);
      }
    }, 500);
    return () => { alive = false; clearTimeout(t); setQuoteLoading(false); };
  }, [
    form.arrival_address, form.arrival_coord,
    form.pickup_address, form.pickup_coord,
    form.vehicle_type, form.direction,
    form.custom_price, form.dispatch_commission_pct,
  ]);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  // Dispatch accounts get a different UX (custom pickup, custom price, extras)
  const isDispatch = (profile?.account_type || "").toLowerCase() === "dispatch";

  const toggleExtra = (val) => {
    setForm((f) => {
      const list = Array.isArray(f.extras) ? f.extras : [];
      return { ...f, extras: list.includes(val) ? list.filter((x) => x !== val) : [...list, val] };
    });
  };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = {
        ...form,
        luggage_count: Number(form.luggage_count),
        passenger_count: Number(form.passenger_count),
        receptionist_id: form.receptionist_id || null,
        scheduled_at: form.booking_type === "scheduled" && form.scheduled_at
          ? new Date(form.scheduled_at).toISOString()
          : null,
        // Dispatch-only — sent only when relevant, otherwise the backend
        // ignores these fields (they default to None server-side).
        pickup_address: isDispatch ? form.pickup_address : null,
        pickup_coord: isDispatch ? form.pickup_coord : null,
        custom_price: isDispatch && form.custom_price !== ""
          ? Number(form.custom_price)
          : null,
        dispatch_commission_pct: isDispatch
          ? Math.max(0, Math.min(20, Number(form.dispatch_commission_pct ?? 20)))
          : null,
        extras: isDispatch && form.extras?.length ? form.extras : null,
      };
      if (payload.booking_type === "scheduled" && !payload.scheduled_at) {
        toast.error("Merci d'indiquer la date et l'heure de la réservation");
        setLoading(false);
        return;
      }
      if (isDispatch && (!payload.pickup_address || payload.pickup_address.trim().length < 3)) {
        toast.error("Merci d'indiquer l'adresse de départ.");
        setLoading(false);
        return;
      }
      const { data } = await api.post("/hotel/bookings", payload);
      toast.success(payload.booking_type === "scheduled" ? "Course programmée" : "Course réservée");
      // Immediately seed the activeBooking so the ClientHero switches to
      // the sonar radar without waiting for the 12 s poll cycle — exactly
      // like the client dashboard does on its own submit.
      if (data?.booking) setActiveBooking(data.booking);
      setForm(defaultForm);
      try { sessionStorage.removeItem(BOOKING_DRAFT_KEY); } catch (_) {}
      // Scroll the page back to the top so the receptionist immediately
      // sees the radar (which lives at the top of the dashboard).
      try { window.scrollTo({ top: 0, behavior: "smooth" }); } catch (_) {}
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setLoading(false); }
  };

  const copyLink = (link) => {
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="relative">
      {/* Animated hero (idle / searching / tracking) — same component as
          the client dashboard. Sits at the top of the page as a visible
          band. Switches to the sonar radar automatically when a hotel-
          created course is still pending a driver, then to the tracking
          route once a chauffeur accepts. */}
      <div className="relative h-[340px] w-full overflow-hidden border-b border-border/50">
        {profile?.background_image_url ? (
          <>
            <div
              className="absolute inset-0 bg-cover bg-center"
              style={{ backgroundImage: `url(${profile.background_image_url})` }}
              data-testid="hotel-dashboard-bg"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/40 to-background/95" />
            {profile.logo_url && (
              <img
                src={profile.logo_url}
                alt="Logo"
                className="absolute right-6 top-6 h-12 w-auto rounded-xl bg-white/90 p-1.5 shadow-lg backdrop-blur"
                data-testid="hotel-dashboard-logo"
              />
            )}
          </>
        ) : (
          <ClientHero mode={heroMode} driverActive={false} />
        )}
      </div>
      <div
        className="mx-auto max-w-screen-2xl px-4 sm:px-6 lg:px-10"
        style={{ paddingTop: "max(32px, calc(env(safe-area-inset-top) + 16px))" }}
        data-testid="hotel-dashboard"
      >
        {/* Menu profil épinglé en haut à droite (fixed) — respecte le
            safe-area et reste toujours accessible pendant le scroll. */}
        <div
          className="fixed right-3 z-40"
          style={{ top: "max(12px, calc(env(safe-area-inset-top) + 6px))" }}
        >
          <HotelProfileMenu
            onOpenHelp={async () => {
              // Ouvre WhatsApp directement — plus rapide et moins bruyant
              // qu'un tel: qui interrompt tout de suite. Sur mobile natif
              // on passe par Capacitor Browser (WKWebView iOS bloque
              // window.open).
              const waUrl = "https://wa.me/33764406342?text=" + encodeURIComponent("Bonjour Saor, j'ai besoin d'aide.");
              try {
                const { Browser } = await import("@capacitor/browser");
                await Browser.open({ url: waUrl });
              } catch (_) {
                window.open(waUrl, "_blank", "noopener,noreferrer");
              }
            }}
            onOpenPublicLink={() => {
              const btn = document.querySelector('[data-testid="hotel-public-link-btn"]');
              if (btn && btn.click) btn.click();
            }}
          />
        </div>

        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-primary">{isDispatch ? "Dispatch" : "Tableau de bord"}</p>
            <h1 className="mt-2 font-[Outfit] text-3xl font-normal tracking-tight sm:text-4xl">
              {profile?.hotel_name || (isDispatch ? "Dispatch" : "Votre hôtel")}
            </h1>
            {!isDispatch && (
              <p className="mt-1 text-sm text-muted-foreground flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" /> {profile?.hotel_address}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 pr-14">
            <PublicLinkButton isAirbnb={(profile?.account_type || "").toLowerCase() === "airbnb"} />
            <EnableNotificationsButton size="sm" />
          </div>
        </div>

        <HotelNavPills active="book" />

        {/* When a course is already pending acceptance, hide the booking
            form and show a clean "searching" panel so the receptionist
            doesn't accidentally double-book while a chauffeur is being
            assigned. The big radar sits in the top hero band; this panel
            below echoes the booking details so they're not lost. */}
        {activeBooking && activeBooking.status === "pending" ? (
          <div
            className="mt-8 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.05] to-card p-6 sm:p-8"
            data-testid="hotel-searching-panel"
          >
            <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-primary/70">
              Recherche en cours
            </p>
            <h2 className="mt-1 font-[Outfit] text-2xl font-semibold tracking-tight text-foreground">
              On contacte les chauffeurs disponibles
            </h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Vous serez redirigé vers l&apos;historique dès qu&apos;un chauffeur accepte. Le radar tourne ↑.
            </p>
            <div className="mt-5 grid gap-4 sm:grid-cols-2">
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">Course</p>
                <p className="mt-1 text-sm font-medium text-foreground">{activeBooking.client_name}</p>
                <p className="mt-0.5 truncate text-xs text-muted-foreground">
                  → {activeBooking.arrival_address}
                </p>
              </div>
              <div className="rounded-xl border border-border bg-card p-4">
                <p className="text-[10px] font-medium uppercase tracking-widest text-muted-foreground">Tarif estimé</p>
                <p className="mt-1 font-[Outfit] text-xl font-semibold tabular-nums text-primary">
                  {Number(activeBooking.price || 0).toFixed(2)} €
                </p>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              size="lg"
              className="mt-5 rounded-full"
              onClick={() => nav("/dashboard/hotel/history")}
              data-testid="hotel-searching-history-cta"
            >
              Voir mes courses <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </div>
        ) : (
        <div className="mt-8 grid gap-8 md:grid-cols-12">
          <form onSubmit={submit} className="md:col-span-8 space-y-5 rounded-2xl border border-border bg-card p-5 shadow-sm sm:space-y-6 sm:p-8" data-testid="quick-booking-form">
            {!isDispatch && (
              <div className="grid grid-cols-2 gap-2" data-testid="direction-picker">
                {[
                  { value: "from_hotel", title: "Départ hôtel", desc: "Pick-up à l'hôtel", icon: <ArrowUpFromLine className="h-4 w-4" /> },
                  { value: "to_hotel", title: "Retour hôtel", desc: "Drop à l'hôtel", icon: <ArrowDownToLine className="h-4 w-4" /> },
                ].map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, direction: d.value }))}
                    className={`flex items-center gap-3 rounded-xl border p-3 text-left transition-all ${
                      form.direction === d.value
                        ? "border-primary bg-primary/5 ring-2 ring-primary/20"
                        : "border-border bg-card hover:border-primary/40"
                    }`}
                    data-testid={`direction-${d.value}`}
                  >
                    <span className={`grid h-9 w-9 place-items-center rounded-full ${form.direction === d.value ? "bg-primary text-primary-foreground" : "bg-secondary/60 text-muted-foreground"}`}>
                      {d.icon}
                    </span>
                    <span>
                      <p className="text-sm font-medium">{d.title}</p>
                      <p className="mt-0.5 text-[11px] text-muted-foreground">{d.desc}</p>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {isDispatch ? (
              <Field label="Adresse de départ" required>
                <AddressAutocomplete
                  value={form.pickup_address}
                  onChange={(v) => setForm((f) => ({ ...f, pickup_address: v, pickup_coord: null }))}
                  onSelect={(text, coord) => setForm((f) => ({ ...f, pickup_address: text, pickup_coord: coord }))}
                  required
                  region={profile?.region}
                  placeholder="Adresse précise de prise en charge"
                  data-testid="dispatch-pickup"
                />
              </Field>
            ) : (
              <div className="rounded-lg border border-border bg-secondary/40 p-4 text-sm">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" /> {form.direction === "to_hotel" ? "Arrivée" : "Départ"}
                </div>
                <p className="mt-1 font-medium text-foreground">{profile?.hotel_address || "..."}</p>
              </div>
            )}

            <Field label={isDispatch ? "Adresse d'arrivée" : (form.direction === "to_hotel" ? "Adresse de départ du client" : "Adresse d'arrivée")} required>
              <AddressAutocomplete
                value={form.arrival_address}
                onChange={(v) => setForm((f) => ({ ...f, arrival_address: v, arrival_coord: null }))}
                onSelect={(text, coord) => setForm((f) => ({ ...f, arrival_address: text, arrival_coord: coord }))}
                required
                region={profile?.region}
                proximityCoord={profile?.coord}
                placeholder={isDispatch ? "Adresse précise d'arrivée" : (form.direction === "to_hotel" ? "Aéroport Nice Côte d'Azur T2 (lieu où prendre le client)" : "Aéroport Nice Côte d'Azur T2")}
                data-testid="book-arrival"
              />
            </Field>

            <Field label="Quand ?" required>
              <div className="grid grid-cols-2 gap-2" data-testid="booking-type-picker">
                {[
                  { value: "immediate", icon: Sparkles, label: "Immédiate" },
                  { value: "scheduled", icon: Clock, label: "Programmée" },
                ].map((b) => {
                  const Icon = b.icon;
                  return (
                    <button
                      key={b.value}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, booking_type: b.value }))}
                      className={`inline-flex items-center justify-center gap-1.5 rounded-full border px-3 py-2 text-sm transition-colors ${
                        form.booking_type === b.value
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-card text-foreground/70 hover:border-primary/40"
                      }`}
                      data-testid={`booking-type-${b.value}`}
                    >
                      <Icon className="h-4 w-4" />
                      {b.label}
                    </button>
                  );
                })}
              </div>
              {form.booking_type === "scheduled" && (
                <Input
                  type="datetime-local"
                  value={form.scheduled_at}
                  min={new Date(Date.now() + 10 * 60 * 1000).toISOString().slice(0, 16)}
                  onChange={upd("scheduled_at")}
                  required
                  className="mt-3"
                  data-testid="book-scheduled-at"
                />
              )}
            </Field>

            {receptionists.length > 0 && (
              <Field label="Réceptionniste en service (facultatif)">
                <div className="flex flex-wrap items-center gap-2" data-testid="receptionist-picker">
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, receptionist_id: "" }))}
                    data-testid="receptionist-none"
                    className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                      !form.receptionist_id
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-foreground/70 hover:border-primary/40"
                    }`}
                  >
                    Aucun
                  </button>
                  {receptionists.map((r) => {
                    const active = form.receptionist_id === r.id;
                    return (
                      <button
                        key={r.id}
                        type="button"
                        onClick={() => setForm((f) => ({ ...f, receptionist_id: r.id }))}
                        data-testid={`receptionist-pick-${r.id}`}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                          active
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-card text-foreground/80 hover:border-primary/40"
                        }`}
                      >
                        <UserCircle2 className="h-3.5 w-3.5" />
                        {r.name}
                      </button>
                    );
                  })}
                </div>
              </Field>
            )}

            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Nom du client" required><Input value={form.client_name} onChange={upd("client_name")} required data-testid="book-client-name" /></Field>
              <Field label="Téléphone client">
                <PhoneInput
                  value={form.client_phone}
                  onChange={(v) => setForm((f) => ({ ...f, client_phone: v }))}
                  data-testid="book-client-phone"
                />
              </Field>
            </div>
            <div className="grid gap-5 sm:grid-cols-2">
              <Field label="Bagages" required>
                <Input type="number" min={0} max={20} value={form.luggage_count} onChange={upd("luggage_count")} required data-testid="book-luggage" />
              </Field>
              <Field label="Personnes" required>
                <Input type="number" min={1} max={20} value={form.passenger_count} onChange={upd("passenger_count")} required data-testid="book-passengers" />
              </Field>
            </div>
            <Field label="Type de véhicule" required>
              <div className="grid grid-cols-3 gap-2" data-testid="vehicle-type-picker">
                {[
                  { value: "sedan", label: "Berline" },
                  { value: "premium_sedan", label: "Premium" },
                  { value: "van", label: "Van" },
                ].map((v) => (
                  <button
                    key={v.value}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, vehicle_type: v.value }))}
                    className={`inline-flex items-center justify-center gap-1.5 rounded-full border px-3 py-2 text-sm transition-colors ${
                      form.vehicle_type === v.value
                        ? "border-primary bg-primary text-primary-foreground"
                        : "border-border bg-card text-foreground/70 hover:border-primary/40"
                    }`}
                    data-testid={`vehicle-${v.value}`}
                  >
                    {v.label}
                  </button>
                ))}
              </div>
            </Field>

            {isDispatch && (
              <Field label="Options client (facultatif)">
                <div className="flex flex-wrap gap-2" data-testid="extras-picker">
                  {EXTRA_OPTIONS.map((x) => {
                    const active = form.extras?.includes(x.value);
                    return (
                      <button
                        key={x.value}
                        type="button"
                        onClick={() => toggleExtra(x.value)}
                        className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs transition-colors ${
                          active
                            ? "border-primary bg-primary text-primary-foreground"
                            : "border-border bg-card text-foreground/70 hover:border-primary/40"
                        }`}
                        data-testid={`extra-${x.value}`}
                      >
                        {active && <Check className="h-3 w-3" />}
                        {x.label}
                      </button>
                    );
                  })}
                </div>
              </Field>
            )}

            {isDispatch && (
              <Field label="Tarif fixé par le dispatch (€)">
                <Input
                  type="number"
                  min={0}
                  step={1}
                  value={form.custom_price}
                  onChange={upd("custom_price")}
                  placeholder="Ex : 65"
                  data-testid="book-custom-price"
                />
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Laissez vide pour utiliser le tarif SAOR automatique.
                </p>
              </Field>
            )}

            {isDispatch && (
              <Field label={`Votre commission : ${form.dispatch_commission_pct ?? 20}%`}>
                <input
                  type="range"
                  min={0}
                  max={20}
                  step={1}
                  value={form.dispatch_commission_pct ?? 20}
                  onChange={(e) => setForm((f) => ({ ...f, dispatch_commission_pct: Number(e.target.value) }))}
                  className="w-full accent-primary"
                  data-testid="book-dispatch-pct"
                />
                <div className="mt-2 grid grid-cols-3 gap-2 text-[11px]">
                  {(() => {
                    const dPct = Number(form.dispatch_commission_pct ?? 20);
                    const sPct = +(10 - dPct / 4).toFixed(2);
                    const drvPct = +(100 - sPct - dPct).toFixed(2);
                    return (
                      <>
                        <div className="rounded-md border border-border bg-secondary/40 p-2 text-center">
                          <p className="text-muted-foreground">Dispatch</p>
                          <p className="mt-0.5 font-medium text-foreground">{dPct} %</p>
                        </div>
                        <div className="rounded-md border border-border bg-secondary/40 p-2 text-center">
                          <p className="text-muted-foreground">SAOR</p>
                          <p className="mt-0.5 font-medium text-foreground">{sPct} %</p>
                        </div>
                        <div className="rounded-md border border-border bg-secondary/40 p-2 text-center">
                          <p className="text-muted-foreground">Chauffeur</p>
                          <p className="mt-0.5 font-medium text-foreground">{drvPct} %</p>
                        </div>
                      </>
                    );
                  })()}
                </div>
                <p className="mt-1 text-[11px] text-muted-foreground">
                  Faites glisser pour ajuster votre commission (max 20 %).
                  La part SAOR varie selon votre choix (10 % à dispatch 0 %, 5 % à dispatch 20 %).
                </p>
              </Field>
            )}

            <Field label="Notes (facultatif)">
              <Textarea rows={2} value={form.notes} onChange={upd("notes")} data-testid="book-notes" />
            </Field>

            {/* Live price preview — mirrors the client dashboard so the
                receptionist sees the price BEFORE clicking "Réserver". */}
            <div
              className="flex flex-wrap items-center justify-between gap-4 rounded-2xl border border-primary/20 bg-gradient-to-br from-primary/[0.05] to-card px-5 py-4"
              data-testid="hotel-price-preview"
            >
              <div className="min-w-0">
                <p className="text-[10px] font-medium uppercase tracking-[0.28em] text-primary/70">
                  Tarif estimé
                </p>
                {!form.arrival_coord ? (
                  <p className="mt-1 text-sm text-muted-foreground">
                    Saisis une adresse d&apos;arrivée pour voir le prix
                  </p>
                ) : quoteLoading ? (
                  <p className="mt-1 flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                    Calcul en cours…
                  </p>
                ) : quote?.ready && Number(quote.price) > 0 ? (
                  <div className="mt-1 flex items-baseline gap-2">
                    <span className="font-[Outfit] text-3xl font-semibold tabular-nums text-foreground" data-testid="hotel-price-value">
                      {Number(quote.price).toFixed(2)} €
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {Number(quote.distance_km || 0).toFixed(1)} km
                      {quote.fixed_zone_label && ` · ${quote.fixed_zone_label}`}
                      {Number(quote.event_surge_pct || 0) > 0 && ` · +${quote.event_surge_pct}% événement`}
                      {quote.custom_price_applied && " · tarif négocié"}
                    </span>
                  </div>
                ) : (
                  <p className="mt-1 text-sm text-muted-foreground">
                    Tarif indisponible — réessaie dans quelques instants
                  </p>
                )}
              </div>
              <Button
                type="submit"
                size="lg"
                disabled={loading}
                className="rounded-full bg-primary px-8 hover:bg-primary/90"
                data-testid="book-submit"
              >
                {loading ? "Réservation..." : <>Réserver le chauffeur <ArrowRight className="ml-2 h-4 w-4" /></>}
              </Button>
            </div>
          </form>

          <aside className="md:col-span-4 space-y-4">
            <div className="rounded-2xl border border-border bg-primary p-6 text-primary-foreground">
              <p className="text-xs uppercase tracking-widest text-white/70">Conseil</p>
              <p className="mt-2 text-sm leading-relaxed text-white/90">
                {isDispatch
                  ? "Renseignez précisément les deux adresses et le téléphone du client. Vous pouvez fixer un tarif personnalisé pour cette course."
                  : "Renseignez précisément l'adresse d'arrivée et le téléphone du client pour fluidifier la prise en charge par le chauffeur."}
              </p>
            </div>
            {!isDispatch && (
              <Info row={form.direction === "to_hotel" ? "Adresse d'arrivée pré-remplie (votre hôtel)" : "Adresse de départ pré-remplie (votre hôtel)"} />
            )}
            {isDispatch && (
              <Info row="Vous touchez 20 % de commission sur chaque course apportée." />
            )}
          </aside>
        </div>
        )}
      </div>

      <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) { setConfirm(null); nav("/dashboard/hotel/history"); } }}>
        <DialogContent data-testid="booking-confirm-dialog" className="overflow-hidden">
          <DialogHeader className="sr-only">
            <DialogTitle>Course réservée</DialogTitle>
            <DialogDescription>Votre demande a bien été enregistrée.</DialogDescription>
          </DialogHeader>
          {confirm && (
            <div className="flex flex-col items-center py-6 text-center">
              {/* Logo Saor animated */}
              <div className="relative animate-in zoom-in-50 fade-in duration-500">
                <div className="relative grid h-20 w-20 place-items-center rounded-full bg-primary shadow-lg">
                  <span className="font-[Outfit] text-4xl font-light text-primary-foreground">S</span>
                  <span className="absolute -right-1 -bottom-1 h-4 w-4 rounded-full bg-accent ring-4 ring-card" />
                </div>
                {/* Soft halo */}
                <span className="absolute inset-0 -z-10 animate-ping-slow rounded-full bg-primary/20" aria-hidden="true" />
              </div>

              <h2 className="mt-5 font-[Outfit] text-3xl font-normal tracking-tight animate-in slide-in-from-bottom-2 fade-in duration-500 delay-100">
                Saor
              </h2>

              <p className="mt-4 inline-flex items-center gap-2 rounded-full border border-[hsl(var(--success))]/30 bg-[hsl(var(--success))]/10 px-4 py-1.5 text-sm font-medium text-[hsl(var(--success))] animate-in slide-in-from-bottom-2 fade-in duration-500 delay-200">
                <Check className="h-4 w-4" /> Course réservée
              </p>

              <p className="mt-3 max-w-[260px] text-sm text-muted-foreground animate-in fade-in duration-700 delay-300">
                Un chauffeur sera notifié et vous verrez son ETA en temps réel dès qu&apos;il accepte.
              </p>

              <div className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl border border-border bg-secondary/30 px-4 py-3 text-sm animate-in fade-in duration-700 delay-300">
                <span className="text-muted-foreground">Prix estimé</span>
                <span className="font-[Outfit] text-lg font-semibold text-primary" data-testid="confirm-price">
                  {confirm.booking.price} €
                </span>
                <span className="text-xs text-muted-foreground">· {confirm.booking.vehicle_type_label}</span>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

export function HotelNavPills({ active }) {
  const item = (key, to, Icon, label, testid) => (
    <Link
      to={to}
      data-testid={testid}
      className={`inline-flex items-center justify-center gap-1.5 rounded-full px-2 py-2 text-xs sm:text-sm transition-colors ${
        active === key
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="h-3.5 w-3.5 flex-none" />
      <span className="truncate">{label}</span>
    </Link>
  );
  return (
    <div
      className="mt-6 grid w-full grid-cols-3 gap-1 rounded-full border border-border bg-secondary/60 p-1 sm:w-auto sm:inline-grid"
      data-testid="hotel-nav-pills"
    >
      {item("book", "/dashboard/hotel", Sparkles, "Réservation", "pill-book")}
      {item("multi", "/dashboard/hotel/multi-destination", Route, "Multi-destination", "pill-multi")}
      {item("history", "/dashboard/hotel/history", History, "Historique", "pill-history")}
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div className="space-y-2">
      <Label className="text-sm">{label} {required && <span className="text-accent">*</span>}</Label>
      {children}
    </div>
  );
}
function Info({ row }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border bg-card p-4 text-sm">
      <div className="h-1.5 w-1.5 rounded-full bg-accent" /> {row}
    </div>
  );
}

/**
 * PublicLinkButton — header CTA that opens a dialog with the hotel's
 * shareable public booking URL. Lazy-loaded (slug minted on first open).
 * Includes copy-to-clipboard, SMS/WhatsApp shortcuts and a fallback QR
 * code via a public service (no extra dependency).
 */
function PublicLinkButton({ isAirbnb }) {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState(null); // {url, slug, enabled}
  const [loading, setLoading] = useState(false);
  const [toggling, setToggling] = useState(false);

  const fetchLink = async () => {
    setLoading(true);
    try {
      const res = await api.get("/hotel/public-link");
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e) || "Impossible de récupérer le lien public");
    } finally {
      setLoading(false);
    }
  };

  const openDialog = async () => {
    setOpen(true);
    if (!data) await fetchLink();
  };

  const toggleEnabled = async () => {
    setToggling(true);
    try {
      const res = await api.put("/hotel/public-link/toggle");
      setData((d) => ({ ...d, enabled: res.data.enabled }));
      toast.success(res.data.enabled ? "Lien public réactivé" : "Lien public désactivé");
    } catch (e) {
      toast.error(formatApiError(e) || "Action impossible");
    } finally {
      setToggling(false);
    }
  };

  const qrSrc = data?.url
    ? `https://api.qrserver.com/v1/create-qr-code/?size=240x240&data=${encodeURIComponent(data.url)}`
    : null;

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        onClick={openDialog}
        className="rounded-full border-primary/40 text-primary hover:bg-primary/10"
        data-testid="hotel-public-link-button"
      >
        <Link2 className="mr-1.5 h-3.5 w-3.5" /> Mon lien {isAirbnb ? "Airbnb" : "hôtel"}
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="hotel-public-link-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Link2 className="h-5 w-5 text-primary" /> Lien de réservation à partager
            </DialogTitle>
            <DialogDescription>
              Imprimez le QR dans les chambres ou envoyez le lien aux invités par SMS/WhatsApp. Le client réserve en 30 s sans créer de compte et la course apparaît directement chez vous.
            </DialogDescription>
          </DialogHeader>
          {loading || !data ? (
            <div className="grid place-items-center py-8"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (
            <div className="space-y-4 py-2">
              <div className="rounded-2xl border-2 border-primary/30 bg-primary/5 p-4">
                <p className="text-[10px] font-semibold uppercase tracking-wider text-primary">Votre URL publique</p>
                <p className="mt-2 break-all rounded-md bg-foreground/5 px-3 py-2 font-mono text-xs leading-relaxed">{data.url}</p>
              </div>

              {qrSrc && (
                <div className="grid place-items-center rounded-2xl border border-border bg-card p-4">
                  <div className="flex items-center gap-2 text-xs text-muted-foreground"><QrCode className="h-4 w-4" /> QR à imprimer (chambres, réception)</div>
                  <img src={qrSrc} alt="QR code lien public" className="mt-3 h-44 w-44 rounded-md bg-white p-2" data-testid="hotel-public-link-qr" />
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                <Button
                  size="sm"
                  onClick={async () => {
                    try {
                      await navigator.clipboard.writeText(data.url);
                      toast.success("Lien copié");
                    } catch (_) {
                      toast.error("Copie impossible — sélectionnez le lien manuellement");
                    }
                  }}
                  className="bg-primary hover:bg-primary/90"
                  data-testid="hotel-public-link-copy"
                >
                  <Copy className="mr-1.5 h-4 w-4" /> Copier
                </Button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`Bonjour, voici votre lien Saor pour réserver un chauffeur : ${data.url}`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="hotel-public-link-whatsapp"
                >
                  <Button size="sm" variant="outline">📱 WhatsApp</Button>
                </a>
                <a
                  href={`sms:?body=${encodeURIComponent(`Bonjour, voici votre lien Saor pour réserver un chauffeur : ${data.url}`)}`}
                  data-testid="hotel-public-link-sms"
                >
                  <Button size="sm" variant="outline">💬 SMS</Button>
                </a>
                {qrSrc && (
                  <a href={qrSrc} download="saor-qr.png" data-testid="hotel-public-link-download-qr">
                    <Button size="sm" variant="outline"><QrCode className="mr-1.5 h-4 w-4" /> Télécharger QR</Button>
                  </a>
                )}
              </div>

              <div className="flex items-center justify-between rounded-2xl border border-border bg-secondary/30 px-4 py-3">
                <div className="min-w-0 text-xs">
                  <p className="font-medium">Statut du lien</p>
                  <p className="text-muted-foreground">{data.enabled ? "✅ Actif — les invités peuvent réserver" : "🚫 Désactivé — l'URL renvoie une erreur"}</p>
                </div>
                <Button
                  size="sm"
                  variant={data.enabled ? "outline" : "default"}
                  onClick={toggleEnabled}
                  disabled={toggling}
                  data-testid="hotel-public-link-toggle"
                >
                  {toggling ? "…" : data.enabled ? "Désactiver" : "Réactiver"}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}

